import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { getAIStudioToolScopeKey, getAIToolDisabledTooltip, isAIToolScopeDisabled } from 'src/shared/aiToolScopeStatus';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { T, toGlobalID, urlToFile } from 'src/shared/helpers';
import styled, { keyframes } from 'styled-components';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { LocalModel3DItem } from '../../assetLibrary/assetItem';
import { AssetLibrary } from '../../assetLibrary/assetLibrary';
import { TOOLS } from '../../constants';
import { useAIStudioGeneration } from '../../hooks/useAIStudioGeneration';
import { getInitialSelectionsForTool, updateToolState, useAIStudioState } from '../../hooks/useAIStudioState';
import { useDefaultSelections } from '../../hooks/useDefaultSelections';
import { useIframeMessages } from '../../hooks/useIframeMessages';
import { useProductScene } from '../../hooks/useProductScene';
import { videoGenerationModelsQuery } from '../../queries/__generated__/videoGenerationModelsQuery.graphql';
import { VideoGenerationModelsQuery } from '../../queries/videoGenerationModels';
import { InputImageData, MODEL3D_TOOL_IDS, ToolType } from '../../types';
import { getSideImageAsBase64 } from '../../utils/imageHelpers';
import { useAIStudioProduct } from '../context/productContext';
import { useViewer3DCapture, Viewer3DProvider } from '../context/viewer3DContext';
import { AssetPickerDialog } from '../dialogs/assetPickerDialog';
import { FairUseLimitDialog } from '../dialogs/fairUseLimitDialog';
import { Local3DPreviewDialog } from '../dialogs/local3DPreviewDialog';
import ProductCatalogDialog from '../dialogs/productCatalogDialog';
import { AIStudioHomepage, CREATIVE_STUDIO_TOOL_CARDS, MODEL3D_SUBCARDS } from '../navigation/aiStudioHomepage';
import { Header } from '../navigation/header';
import { Text3DWorkspaceView } from '../navigation/text3DWorkspaceView';
import { PresetDropdownValue } from '../presets/presetDropdown';
import { AutoCapture3D } from '../shared/autoCapture3D';
import { BackToProductButton } from '../shared/backToProductButton';
import ProductIframe from '../shared/productIframe';
import { aiStudioContentEnvironmentQuery } from './__generated__/aiStudioContentEnvironmentQuery.graphql';
import { aiStudioContentToolScopeStatusQuery } from './__generated__/aiStudioContentToolScopeStatusQuery.graphql';
import { BottomPanel } from './bottomPanel';
import { CanvasArea } from './canvasArea';
import { SettingsPanel } from './settingsPanel';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${(props) => props.theme.app.backgroundColor};
    font-family: ${(props) => props.theme.app.font};
    position: relative;

    button,
    input,
    select,
    textarea {
        font-family: inherit;
    }
`;

const ContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    min-height: 0;
`;

const CenterColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%);
`;

const MainContent = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
`;

const fadeInWorkspace = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const Model3DMain = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    animation: ${fadeInWorkspace} 0.2s ease;
`;

interface AIStudioContentProps {
    onClose: () => void;
    initialTool?: ToolType;
    autoCapture3D?: boolean;
}

/**
 * Detects 3D scene availability for a catalog-selected product.
 * Must only be rendered when productId is defined (avoids conditional hook issue in useProductScene).
 */
const CatalogProduct3DDetector: FC<{ productId: number }> = ({ productId }) => {
    const sceneHas3D = useProductScene(productId);
    const { model3D, updateProduct } = useAIStudioProduct();
    useEffect(() => {
        updateProduct({ has3D: !!model3D || sceneHas3D });
    }, [model3D, sceneHas3D, updateProduct]);
    return null;
};

/**
 * Fetches zakekeEnvironment from the product and pushes it into the product context.
 * Covers DAM (VIEWER) and customizer (CUSTOMIZER) paths where ProductIframe is not used.
 */
const ProductEnvironmentDetector: FC<{ productId: number }> = ({ productId }) => {
    const data = useLazyLoadQuery<aiStudioContentEnvironmentQuery>(
        graphql`
            query aiStudioContentEnvironmentQuery($productId: ID!) {
                model(id: $productId) {
                    zakekeEnvironment
                }
            }
        `,
        { productId: toGlobalID(productId, 'Model')! }
    );
    const { updateProduct } = useAIStudioProduct();
    const env = data.model?.zakekeEnvironment;
    useEffect(() => {
        if (env && env !== 'UNKNOWN') {
            updateProduct({ environment: env });
        }
    }, [env, updateProduct]);
    return null;
};

/** Inner component that uses Viewer3DCapture context */
const AIStudioContentInner: FC<AIStudioContentProps> = ({ onClose, initialTool, autoCapture3D }) => {
    const { sideImages, has3D, productId, model3D, model3DRootUrl, updateProduct, resetProduct, environment } = useAIStudioProduct();
    const viewer3DCapture = useViewer3DCapture();
    const { showDialog, closeDialog } = useDialogManager();
    const isAiStudio3DEnabled = useFeatureFlag('aiStudio3D');
    const visibleTools = useMemo(() => TOOLS.filter((t) => !MODEL3D_TOOL_IDS.includes(t.id as ToolType) || isAiStudio3DEnabled), [isAiStudio3DEnabled]);
    const scopeStatusData = useLazyLoadQuery<aiStudioContentToolScopeStatusQuery>(
        graphql`
            query aiStudioContentToolScopeStatusQuery {
                aiStudioImageScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_IMAGE) {
                    isDisabled
                }
                aiStudioVideoScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_VIDEO) {
                    isDisabled
                }
                aiStudioUpscalerScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_UPSCALER) {
                    isDisabled
                }
                aiStudio3DModelScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_3D_MODEL) {
                    isDisabled
                }
            }
        `,
        {},
        { fetchPolicy: 'store-and-network' }
    );
    const disabledTooltipByScopeKey = useMemo(
        () => ({
            image: isAIToolScopeDisabled(scopeStatusData.aiStudioImageScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            video: isAIToolScopeDisabled(scopeStatusData.aiStudioVideoScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            upscaler: isAIToolScopeDisabled(scopeStatusData.aiStudioUpscalerScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            model3d: isAIToolScopeDisabled(scopeStatusData.aiStudio3DModelScopeStatus) ? getAIToolDisabledTooltip() : undefined
        }),
        [scopeStatusData]
    );
    const getDisabledTooltipForTool = useCallback(
        (toolId: string) => {
            const scopeKey = getAIStudioToolScopeKey(toolId);
            return scopeKey ? disabledTooltipByScopeKey[scopeKey] : undefined;
        },
        [disabledTooltipByScopeKey]
    );
    const disabledTooltipByToolId = useMemo(() => {
        const toolIds = new Set<string>([...TOOLS.map((tool) => tool.id), ...MODEL3D_SUBCARDS.map((card) => card.id)]);
        const disabledTooltips: Record<string, string | undefined> = {};
        toolIds.forEach((toolId) => {
            disabledTooltips[toolId] = getDisabledTooltipForTool(toolId);
        });
        return disabledTooltips;
    }, [getDisabledTooltipForTool]);

    // --- Video generation models (needed for initial video options) ---
    const modelsData = useLazyLoadQuery<videoGenerationModelsQuery>(VideoGenerationModelsQuery, {});

    // --- Core state hook ---
    const {
        state,
        setState,
        results,
        selectedImageIndex,
        setSelectedImageIndex,
        inputImages,
        setInputImages,
        isIframeMode,
        setIsIframeMode,
        isFromCatalog,
        setIsFromCatalog,
        viewMode,
        setViewMode,
        currentToolView,
        generationStartedAt,
        globalView,
        currentToolConfig,
        hasProductInput,
        productImageUrl,
        selectionState,
        goToHome,
        goToEditor,
        goToGallery,
        goToLibrary,
        goBack,
        handleToolChange,
        handlePromptChange,
        handlePresetSelect,
        handleDeleteResult,
        handleReturnToCustomizer,
        isGenerating,
        isProductInputEditing,
        setIsProductInputEditing,
        isAutoCapturing,
        setIsAutoCapturing,
        videoOptions,
        setVideoOptions,
        adOptions,
        setAdOptions
    } = useAIStudioState({
        initialTool,
        sideImages,
        has3D,
        productId,
        initialVideoModel: modelsData.videoGenerationModels[0],
        autoCapture3D
    });

    // --- Generation hook ---
    const isInTrialOrTestStore = false;
    const pendingGenerateRef = useRef(false);
    const handleGenerateRef = useRef<() => Promise<void>>(async () => {});

    const handleFairUseLimitReached = useCallback(
        (serviceName: string) => {
            showDialog('fair-use-limit-dialog', <FairUseLimitDialog serviceName={serviceName} onClose={() => closeDialog('fair-use-limit-dialog')} />);
        },
        [showDialog, closeDialog]
    );

    const { handleGenerate, handleStopGeneration, canGenerate } = useAIStudioGeneration({
        state,
        setState,
        inputImages,
        setInputImages,
        sideImages,
        selectedImageIndex,
        viewMode,
        has3D,
        productId,
        viewer3DCapture,
        videoOptions,
        adOptions,
        productImageUrl,
        isInTrialOrTestStore,
        isAutoCapturing,
        onFairUseLimitReached: handleFairUseLimitReached,
        setIsProductInputEditing,
        isProductInputEditing,
        hasProductInput
    });

    // Keep handleGenerateRef up-to-date so the pending-generate effect always calls the latest version
    handleGenerateRef.current = handleGenerate;

    // Auto-trigger generation after navigating from the landing view
    useEffect(() => {
        if (pendingGenerateRef.current && state.globalView === 'tool' && state.selectedTool !== null) {
            pendingGenerateRef.current = false;
            handleGenerateRef.current();
        }
    }, [state.globalView, state.selectedTool]);

    // --- Homepage overlay state (lifted so Header can show tabs) ---
    const [homepageOverlayToolId, setHomepageOverlayToolId] = React.useState<ToolType | null>(null);
    const [homepageOverlayAgentId, setHomepageOverlayAgentId] = React.useState<string | null>(null);

    // Image-to-3d with a product loaded uses the standard Creative Studio workspace
    // (CanvasArea + ProductInputEditor + BottomPanel) so the user can rotate the 3D scene to
    // pick an angle, exactly like upscaler/product-transformer. Text-to-3d and multi-views-to-3d
    // always use the dedicated 3D workspace. `productId` is the same hint used by the existing
    // Creative-Studio-skip-landing logic (e.g. line 305 in `landingTabs`); it goes truthy as
    // soon as the dialog mounts with a product, before the async `useProductScene` query for
    // `has3D` resolves.
    const usesModel3DWorkspace = useCallback(
        (toolId: ToolType | null | undefined) => {
            if (!toolId || !MODEL3D_TOOL_IDS.includes(toolId)) return false;
            if (toolId === 'image-to-3d' && !!productId) return false;
            return true;
        },
        [productId]
    );

    const landingTabs = useMemo(() => {
        if (!homepageOverlayToolId && !homepageOverlayAgentId) return undefined;
        return [
            ...CREATIVE_STUDIO_TOOL_CARDS.filter((card) => visibleTools.some((tool) => tool.id === card.id)).map((card) => ({
                id: card.id,
                name: card.name,
                active: card.id === homepageOverlayToolId,
                disabledTooltip: getDisabledTooltipForTool(card.id),
                onClick: () => {
                    if (getDisabledTooltipForTool(card.id)) return;
                    // When a product is loaded, Creative Studio tools skip their landing and go
                    // straight to the workspace — same behavior as the homepage card click and
                    // the header toolbar click outside of overlay mode. Without this, clicking
                    // a Creative Studio tab from inside a 3D agent landing (or another tool's
                    // landing) would just swap the landing overlay and leave the user on a blank
                    // upload zone instead of the workspace with the product image already loaded.
                    if (productId) {
                        setHomepageOverlayAgentId(null);
                        setHomepageOverlayToolId(null);
                        handleToolChange(card.id);
                        return;
                    }
                    setHomepageOverlayAgentId(null);
                    setHomepageOverlayToolId(card.id);
                }
            })),
            ...(isAiStudio3DEnabled
                ? MODEL3D_SUBCARDS.map((card) => ({
                      id: card.id,
                      name: card.name,
                      active: card.id === homepageOverlayAgentId,
                      disabledTooltip: getDisabledTooltipForTool(card.id),
                      onClick: () => {
                          if (getDisabledTooltipForTool(card.id)) return;
                          // image-to-3d in product mode skips its landing and goes straight to the
                          // Creative Studio workspace (ProductInputEditor + Images/3D View toggle).
                          if (card.id === 'image-to-3d' && productId) {
                              setHomepageOverlayAgentId(null);
                              setHomepageOverlayToolId(null);
                              handleToolChange(card.id);
                              return;
                          }
                          setHomepageOverlayToolId(null);
                          setHomepageOverlayAgentId(card.id);
                      }
                  }))
                : [])
        ];
    }, [homepageOverlayToolId, homepageOverlayAgentId, isAiStudio3DEnabled, visibleTools, getDisabledTooltipForTool, productId, handleToolChange]);

    // --- Model-3D state ---
    const [model3dInitialData, setModel3dInitialData] = React.useState<{
        prompt?: string;
        imageFile?: File;
        imageUrl?: string;
        multiViewReady?: boolean;
        mvFiles?: Array<{ file: File; viewType: string }>;
    }>({});
    const [model3dGeneratedAssets, setModel3dGeneratedAssets] = React.useState<LocalModel3DItem[]>([]);
    const [model3dOpenedAsset, setModel3dOpenedAsset] = React.useState<LocalModel3DItem | null>(null);
    const [local3dPreviewIndex, setLocal3dPreviewIndex] = React.useState<number | null>(null);

    // Tracks every 3D agent the user has visited in this session. We render one Model3DMain
    // per visited agent (with display:none for inactive ones) so each agent has its own
    // Text3DWorkspaceView instance with isolated internal state — switching from a generating
    // agent to another agent shows the destination agent's own state (empty if untouched),
    // not the source agent's in-progress generation. Reset when the user clicks Home.
    const [visited3DAgents, setVisited3DAgents] = React.useState<Set<ToolType>>(new Set());

    // Per-tool snapshot of the user-uploaded / library-picked input images. The shared
    // `inputImages` state always reflects the currently selected tool; on a header tab switch
    // we save the source tool's images here and load the destination tool's images back, so
    // each 2D tool keeps its own inputs and they don't leak across tools.
    const [inputImagesByTool, setInputImagesByTool] = React.useState<Partial<Record<ToolType, InputImageData[]>>>({});

    // Per-agent counter incremented only when the user starts a new 3D generation from the
    // landing view (handleModel3DAgentSelect). It's part of that agent's workspace key so a
    // landing-driven entry forces a fresh remount (letting auto-start fire) while header
    // navigation leaves the key unchanged and preserves any in-progress state.
    const [agentFreshKeys, setAgentFreshKeys] = React.useState<Partial<Record<ToolType, number>>>({});

    React.useEffect(() => {
        if (state.selectedTool && usesModel3DWorkspace(state.selectedTool)) {
            const tool = state.selectedTool as ToolType;
            setVisited3DAgents((prev) => {
                if (prev.has(tool)) return prev;
                const next = new Set(prev);
                next.add(tool);
                return next;
            });
        }
    }, [state.selectedTool, usesModel3DWorkspace]);

    const handleModel3dGenerated = React.useCallback(
        (id: string, agentId: string, label: string, thumbnailUrl?: string, prompt?: string, modelUrl?: string) => {
            // Clear initial data so that if the user switches tabs and comes back the workspace
            // does NOT auto-start a new generation — it just shows the already-generated model.
            setModel3dInitialData({});
            setModel3dGeneratedAssets((prev) => [...prev, { id, label, toolId: agentId, thumbnailUrl, modelUrl, prompt, createdAt: Date.now() }]);
        },
        []
    );

    const handleRenameLocalModel3dAsset = React.useCallback((id: string, newName: string) => {
        setModel3dGeneratedAssets((prev) => prev.map((item) => (item.id === id ? { ...item, label: newName } : item)));
    }, []);

    const handleOpenLocalModel3dAssetIn3D = React.useCallback(
        (item: LocalModel3DItem) => {
            const idx = model3dGeneratedAssets.findIndex((a) => a.id === item.id);
            if (idx >= 0) setLocal3dPreviewIndex(idx);
        },
        [model3dGeneratedAssets]
    );

    // Clear the opened asset when the user navigates to a different tool (or away from any 3D tool)
    React.useEffect(() => {
        if (model3dOpenedAsset && state.selectedTool !== model3dOpenedAsset.toolId) {
            setModel3dOpenedAsset(null);
        }
    }, [state.selectedTool, model3dOpenedAsset]);

    // Header tool-tab handler: when switching tool from the workspace, show the
    // destination tool's empty state (homepage landing overlay) instead of jumping
    // straight into the workspace. This ensures the user always sees a clean
    // empty state with the correct prompt for the target tool.
    const handleHeaderToolChange = React.useCallback(
        (toolId: ToolType) => {
            if (getDisabledTooltipForTool(toolId)) return;

            // 3D agents with a product loaded should always show their landing (empty state
            // with the product image pre-filled) rather than jumping into the workspace's
            // empty 4-cell grid. Mirrors the homepage card-click behavior so the user gets a
            // consistent landing-first flow for 3D agents in product mode. Any visited 3D
            // workspaces remain mounted in the DOM (display:none) so in-progress state is
            // preserved if the user later picks "Start Creating" or switches back via header.
            //
            // Exception: image-to-3d in product mode uses the standard Creative Studio
            // workspace (with ProductInputEditor + Images/3D View toggle) so the user can
            // rotate the 3D scene to pick a different angle before sending — same pattern as
            // upscaler/product-transformer. Falling through to the regular workspace path
            // below handles this.
            if (productId && usesModel3DWorkspace(toolId)) {
                setModel3dInitialData({});
                setModel3dOpenedAsset(null);
                setState((prev) => ({
                    ...prev,
                    selectedTool: null,
                    globalView: 'home' as const,
                    prompt: '',
                    selections: {}
                }));
                setHomepageOverlayToolId(null);
                setHomepageOverlayAgentId(toolId);
                return;
            }

            // Preserve in-progress generations and existing results across tool switches:
            // - 2D tools store generation state in toolStates (hasAnySavedState).
            // - 3D tools (text-to-3d, image-to-3d, multi-views-to-3d) keep generation state
            //   inside per-agent Text3DWorkspaceView instances; visited3DAgents tracks the
            //   ones currently mounted.
            // In any of these cases, skip the landing overlay and switch directly to the
            // destination tool's workspace so the workspace component (and its state) survives.
            const hasAnySavedState = Object.values(state.toolStates).some((ts) => !!ts && (ts.isGenerating || ts.results.length > 0));
            const has3DWorkspaceMounted = visited3DAgents.size > 0;

            if (productId || hasAnySavedState || has3DWorkspaceMounted) {
                if (MODEL3D_TOOL_IDS.includes(toolId)) {
                    setModel3dInitialData({});
                    setModel3dOpenedAsset(null);
                } else {
                    // Heading into a non-3D tool view — clear any leftover landing-overlay
                    // state from a previous 3D-agent toolbar click so it doesn't reappear
                    // the next time the user navigates back to home.
                    setHomepageOverlayAgentId(null);
                    setHomepageOverlayToolId(null);
                }
                if (productId) {
                    handleToolChange(toolId);
                } else {
                    // Save the source tool's inputImages and load the destination tool's
                    // saved inputImages (empty for first visit). This isolates per-tool inputs
                    // so an image uploaded in one tool doesn't leak into another. Done only
                    // outside product mode — when a product is loaded its image is shared.
                    // We treat the presence of sideImages as a product-context hint as well,
                    // so testPage/preview flows still share the side image across tools.
                    const hasProductContext = !!sideImages && sideImages.length > 0;
                    if (hasProductContext) {
                        // Product context: keep inputImages shared across tools.
                    } else {
                        const sourceTool = state.selectedTool;
                        const destinationImages = sourceTool === toolId ? inputImages : (inputImagesByTool[toolId] ?? []);
                        if (sourceTool && sourceTool !== toolId) {
                            setInputImagesByTool((prev) => ({ ...prev, [sourceTool]: inputImages }));
                        }
                        setInputImages(destinationImages);
                    }
                    setState((prev) => ({
                        ...prev,
                        selectedTool: toolId,
                        prompt: '',
                        selections: {},
                        globalView: 'tool' as const,
                        error: null
                    }));
                    setIsProductInputEditing(false);
                }
                return;
            }
            goToHome();
            if (usesModel3DWorkspace(toolId)) {
                setModel3dInitialData({});
                setModel3dOpenedAsset(null);
                setHomepageOverlayToolId(null);
                setHomepageOverlayAgentId(toolId);
            } else {
                setHomepageOverlayAgentId(null);
                setHomepageOverlayToolId(toolId);
            }
        },
        [
            goToHome,
            productId,
            handleToolChange,
            state.toolStates,
            state.selectedTool,
            visited3DAgents,
            sideImages,
            inputImages,
            inputImagesByTool,
            setInputImages,
            setState,
            setIsProductInputEditing,
            getDisabledTooltipForTool,
            usesModel3DWorkspace
        ]
    );

    const handleModel3DAgentSelect = React.useCallback(
        (
            agentId: string,
            prompt?: string,
            imageFile?: File,
            multiViewReady?: boolean,
            imageUrl?: string,
            mvFiles?: Array<{ file: File; viewType: string }>
        ) => {
            if (getDisabledTooltipForTool(agentId)) return;

            setModel3dInitialData({ prompt, imageFile, imageUrl, multiViewReady, mvFiles });
            // Force a fresh mount of this agent's workspace so it picks up the new initial
            // data and its pendingAutoStart effect fires. Other agents' instances are not
            // affected (their fresh keys don't change).
            setAgentFreshKeys((prev) => ({ ...prev, [agentId as ToolType]: (prev[agentId as ToolType] ?? 0) + 1 }));
            handleToolChange(agentId as ToolType);
        },
        [handleToolChange, getDisabledTooltipForTool]
    );

    // --- Generate from landing view ---
    const handleGenerateFromLanding = useCallback(
        (toolId: ToolType, images: InputImageData[], prompt: string, selections: Record<string, PresetDropdownValue | null>) => {
            if (getDisabledTooltipForTool(toolId)) return;

            if (images.length > 0) {
                setInputImages(images);
            }
            // Merge tool defaults under the landing's user-picked selections so canGenerate
            // sees mandatory presets (e.g. virtual-tryon's Model) on the first render and the
            // pending-generate effect doesn't race the async useDefaultSelections fetch.
            const mergedSelections = { ...getInitialSelectionsForTool(toolId), ...selections };
            setState((prev) => ({
                ...updateToolState(prev, toolId, { view: 'gallery' }),
                selectedTool: toolId,
                prompt,
                selections: mergedSelections,
                globalView: 'tool',
                error: null
            }));
            setIsProductInputEditing(false);
            pendingGenerateRef.current = true;
        },
        [setInputImages, setState, setIsProductInputEditing, getDisabledTooltipForTool]
    );

    const handleOpenLibraryForLanding = useCallback(
        (onSelect: (imageUrl: string) => void) => {
            showDialog(
                'asset-picker-dialog-landing',
                <Suspense fallback={null}>
                    <AssetPickerDialog
                        onSelect={(imageUrl) => {
                            closeDialog('asset-picker-dialog-landing');
                            onSelect(imageUrl);
                        }}
                    />
                </Suspense>
            );
        },
        [showDialog, closeDialog]
    );

    // --- Default selections ---
    useDefaultSelections(state.selectedTool, setState);

    // --- Iframe messages ---
    useIframeMessages({
        updateProduct,
        setIsIframeMode,
        setState,
        setSelectedImageIndex,
        setViewMode,
        setIsProductInputEditing,
        setInputImages,
        setIsAutoCapturing
    });

    // Ensure the product image stays in inputImages whenever a product is open. Runs on mount
    // (initial side-image load for testPage 2D) AND re-runs if inputImages loses its fromProduct
    // entry mid-flow — for example, when the user navigates between a 3D agent and a Creative
    // Studio tool via the header toolbar and some transient state change drops the product
    // image. For 3D products without sideImages, we re-arm AutoCapture3D by flipping
    // isAutoCapturing back on; the AutoCapture3D component will then re-render its hidden
    // scene viewer and replace the missing entry once the screenshot is ready.
    // Note: clearProductState (the user's explicit "remove product" action) sets productId to
    // undefined, so the early return below keeps this effect from undoing an intentional removal.
    useEffect(() => {
        if (!productId) return;
        if (inputImages.some((img) => img.fromProduct)) return;
        if (has3D) {
            if (!isAutoCapturing) setIsAutoCapturing(true);
            return;
        }
        if (!sideImages || sideImages.length === 0) return;
        const selectedSide = sideImages[0];
        let cancelled = false;
        getSideImageAsBase64(selectedSide).then((base64) => {
            if (cancelled || !base64) return;
            setInputImages((prev) => {
                if (prev.some((img) => img.fromProduct)) return prev;
                return [
                    ...prev.filter((img) => !img.fromProduct),
                    {
                        id: `input-side-${Date.now()}`,
                        url: base64,
                        name: selectedSide.sideName || 'Product image',
                        fromProduct: true
                    }
                ];
            });
        });
        return () => {
            cancelled = true;
        };
    }, [productId, has3D, sideImages, inputImages, isAutoCapturing, setInputImages, setIsAutoCapturing]);

    // --- Document title ---
    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Zakeke - AI Studio';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    // --- Dialog callbacks (need showDialog/closeDialog JSX) ---
    const handleUseAsInput = useCallback(
        (imageUrl: string) => {
            const applyAsInput = () => {
                setInputImages([{ id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, url: imageUrl, name: 'Input image' }]);
                goToEditor();
                // When invoked from the asset library, navigate back to the tool view
                setState((prev) => (prev.globalView === 'library' ? { ...prev, globalView: 'tool' } : prev));
            };

            const isInProductMode = (sideImages && sideImages.length > 0) || (has3D && productId);

            if (isInProductMode) {
                showDialog(
                    'use-as-input-confirm',
                    <QuestionDialog
                        message={
                            <span>
                                {T._(
                                    'Using this image will replace the current input and exit from the product catalog mode. Do you want to continue?',
                                    'Admin'
                                )}
                            </span>
                        }
                        onYesClick={() => {
                            closeDialog('use-as-input-confirm');
                            applyAsInput();
                        }}
                    />
                );
            } else {
                applyAsInput();
            }
        },
        [sideImages, has3D, productId, showDialog, closeDialog, goToEditor, setState, setInputImages]
    );

    const handleOpenCatalog = useCallback(() => {
        showDialog(
            'product-catalog-dialog',
            <Suspense fallback={null}>
                <ProductCatalogDialog
                    onConfirm={(modelID) => {
                        const applyProduct = () => {
                            closeDialog('product-catalog-dialog');
                            updateProduct({ productId: modelID, model3D: undefined, model3DRootUrl: undefined });
                            setIsIframeMode(true);
                            setIsFromCatalog(true);
                            setViewMode('images');
                        };

                        if (productId) {
                            closeDialog('product-catalog-dialog');
                            showDialog(
                                'switch-product-confirm',
                                <QuestionDialog
                                    message={<span>{T._('All work will be lost. Are you sure you want to switch product?', 'Admin')}</span>}
                                    onYesClick={() => {
                                        closeDialog('switch-product-confirm');
                                        applyProduct();
                                    }}
                                />
                            );
                        } else {
                            applyProduct();
                        }
                    }}
                />
            </Suspense>
        );
    }, [showDialog, closeDialog, updateProduct, productId, setIsIframeMode, setIsFromCatalog, setViewMode]);

    const handleOpenLibraryDialog = useCallback(() => {
        const supportsMultiple = state.selectedTool === 'multi-image-fusion';
        showDialog(
            'asset-picker-dialog',
            <Suspense fallback={null}>
                <AssetPickerDialog
                    onSelect={(imageUrl) => {
                        closeDialog('asset-picker-dialog');
                        const newImage: InputImageData = {
                            id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            url: imageUrl,
                            name: 'Library asset'
                        };
                        if (supportsMultiple) {
                            setInputImages((prev) => [...prev, newImage]);
                        } else {
                            setInputImages([newImage]);
                        }
                    }}
                />
            </Suspense>
        );
    }, [showDialog, closeDialog, state.selectedTool, setInputImages]);

    // Model-3D library picker for File-based slots (multi-view). Fetches the URL client-side.
    const handleOpenLibraryForModel3D = useCallback(
        (onSelect: (file: File) => void) => {
            showDialog(
                'asset-picker-dialog-model3d',
                <Suspense fallback={null}>
                    <AssetPickerDialog
                        onSelect={async (imageUrl) => {
                            closeDialog('asset-picker-dialog-model3d');
                            try {
                                const file = await urlToFile(imageUrl, 'library-image');
                                onSelect(file);
                            } catch {
                                // ignore — if the fetch fails the user can re-pick or upload directly
                            }
                        }}
                    />
                </Suspense>
            );
        },
        [showDialog, closeDialog]
    );

    // Model-3D library picker for the image-to-3d agent. Passes the library URL straight through —
    // the 3D API accepts URL strings directly, avoiding CORS-sensitive client-side fetches.
    const handleOpenImageLibraryForModel3D = useCallback(
        (onSelect: (url: string, name?: string) => void) => {
            showDialog(
                'asset-picker-dialog-image3d',
                <Suspense fallback={null}>
                    <AssetPickerDialog
                        onSelect={(imageUrl) => {
                            closeDialog('asset-picker-dialog-image3d');
                            onSelect(imageUrl);
                        }}
                    />
                </Suspense>
            );
        },
        [showDialog, closeDialog]
    );

    // --- Pending catalog open after tool navigation from homepage ---
    const [pendingCatalogOpen, setPendingCatalogOpen] = React.useState(false);

    React.useEffect(() => {
        if (pendingCatalogOpen && globalView === 'tool') {
            handleOpenCatalog();
            setPendingCatalogOpen(false);
        }
    }, [globalView, pendingCatalogOpen, handleOpenCatalog]);

    const handleOpenCatalogFromHome = React.useCallback(
        (toolId: ToolType) => {
            if (getDisabledTooltipForTool(toolId)) return;

            setPendingCatalogOpen(true);
            handleToolChange(toolId);
        },
        [handleToolChange, getDisabledTooltipForTool]
    );

    /** Resets product state: clears input images, exits iframe/catalog mode, resets product context */
    const clearProductState = useCallback(() => {
        setIsIframeMode(false);
        setInputImages([]);
        setViewMode('images');
        setIsFromCatalog(false);
        updateProduct({
            sideImages: [],
            has3D: false,
            productId: undefined,
            variantId: undefined,
            compositionDocId: undefined,
            docId: undefined,
            model3D: undefined,
            model3DRootUrl: undefined
        });
        resetProduct();
    }, [setIsIframeMode, setInputImages, setViewMode, setIsFromCatalog, updateProduct, resetProduct]);

    const handleBackToTool = useCallback(() => {
        showDialog(
            'back-to-tool-confirm',
            <QuestionDialog
                message={<span>{T._('Any work will be lost. Are you sure you want to go back?', 'Admin')}</span>}
                onYesClick={() => {
                    closeDialog('back-to-tool-confirm');
                    clearProductState();
                }}
            />
        );
    }, [showDialog, closeDialog, clearProductState]);

    const handleRemoveProduct = useCallback(() => {
        showDialog(
            'remove-product-confirm',
            <QuestionDialog
                message={
                    <span>
                        {T._(
                            'This will remove the product input and exit the product mode. The customization or configuration will be lost. Are you sure?',
                            'Admin'
                        )}
                    </span>
                }
                onYesClick={() => {
                    closeDialog('remove-product-confirm');
                    clearProductState();
                }}
            />
        );
    }, [showDialog, closeDialog, clearProductState]);

    // --- Auto-capture 3D handler ---
    const handleAutoCapture3D = useCallback(
        (base64: string) => {
            setInputImages((prev) => [
                ...prev.filter((img) => !img.fromProduct),
                { id: `input-3d-${Date.now()}`, url: base64, name: '3D Capture', fromProduct: true }
            ]);
            setIsAutoCapturing(false);
        },
        [setInputImages, setIsAutoCapturing]
    );

    const handleLibraryToggle = globalView === 'library' ? goBack : goToLibrary;

    return (
        <Container>
            {/* Show back button only when not in library view and not in iframe mode */}
            {globalView !== 'library' && !isIframeMode && productId && (
                <BackToProductButton sideImages={sideImages} onClose={isFromCatalog ? handleReturnToCustomizer : onClose} environment={environment} />
            )}

            {/* Hidden 3D auto-capture when returning from iframe with 3D-only product */}
            {isAutoCapturing && productId && (
                <AutoCapture3D productId={productId} model3D={model3D} model3DRootUrl={model3DRootUrl} onCaptured={handleAutoCapture3D} />
            )}

            {/* Single Header instance - always rendered at the same position to preserve animations */}
            <Header
                tools={visibleTools}
                selectedTool={globalView === 'home' ? null : state.selectedTool}
                onToolChange={handleHeaderToolChange}
                onClose={isIframeMode ? undefined : onClose}
                onGoHome={() => {
                    setHomepageOverlayToolId(null);
                    setHomepageOverlayAgentId(null);
                    setVisited3DAgents(new Set());
                    setAgentFreshKeys({});
                    setInputImagesByTool({});
                    goToHome();
                }}
                onShowLibrary={handleLibraryToggle}
                isLibraryActive={globalView === 'library'}
                isIframeMode={isIframeMode}
                onSwitchProduct={handleOpenCatalog}
                isNavigationDisabled={isAutoCapturing}
                landingTabs={globalView === 'home' ? landingTabs : undefined}
                disabledTooltipByToolId={disabledTooltipByToolId}
            />

            {/* Product iframe — only mounted when product was opened from the catalog inside AI Studio */}
            {isFromCatalog && productId && (
                <Suspense fallback={null}>
                    <ProductIframe productId={productId} visible={isIframeMode} onClose={handleBackToTool} />
                </Suspense>
            )}

            {/* If in iframe mode, override anything as we need to show the iframe. */}
            {!isIframeMode && (
                <>
                    {/* Library view */}
                    {globalView === 'library' && (
                        <Suspense fallback={null}>
                            <AssetLibrary
                                onUseAsInput={handleUseAsInput}
                                localModel3dAssets={model3dGeneratedAssets}
                                onRenameLocalModel3dAsset={handleRenameLocalModel3dAsset}
                                onOpenLocalModel3dAssetIn3D={handleOpenLocalModel3dAssetIn3D}
                            />
                        </Suspense>
                    )}

                    {/* Homepage */}
                    {globalView === 'home' && (
                        <AIStudioHomepage
                            onToolSelect={handleToolChange}
                            onModel3DAgentSelect={handleModel3DAgentSelect}
                            onOpenCatalog={handleOpenCatalogFromHome}
                            onGenerateFromLanding={handleGenerateFromLanding}
                            onSelectFromLibraryForLanding={handleOpenLibraryForLanding}
                            overlayToolId={homepageOverlayToolId}
                            onOverlayToolChange={(toolId) => {
                                if (toolId !== null && getDisabledTooltipForTool(toolId)) return;

                                // If a product is already loaded (opened from testPage/DAM/catalog),
                                // skip the empty-state landing and go directly to the tool workspace
                                // so the product input is shown instead of blank upload zones.
                                if (toolId !== null && productId) {
                                    handleToolChange(toolId);
                                } else {
                                    setHomepageOverlayToolId(toolId);
                                }
                            }}
                            overlayAgentId={homepageOverlayAgentId}
                            onOverlayAgentChange={(agentId) => {
                                if (agentId !== null && getDisabledTooltipForTool(agentId)) return;

                                // image-to-3d in product mode goes straight to the Creative Studio workspace
                                // (ProductInputEditor with Images/3D View toggle) so the user can rotate the
                                // scene and pick an angle, exactly like upscaler/product-transformer.
                                if (agentId !== null && agentId === 'image-to-3d' && productId) {
                                    setHomepageOverlayToolId(null);
                                    setHomepageOverlayAgentId(null);
                                    handleToolChange(agentId as ToolType);
                                    return;
                                }

                                // Other 3D agents (text-to-3d, multi-views-to-3d) always show their
                                // empty-state landing — the product image is propagated into the landing's
                                // pre-filled inputs via Model3DLandingView (e.g. front view for multi-views-to-3d).
                                setHomepageOverlayAgentId(agentId);
                            }}
                            videoOptions={videoOptions}
                            onVideoOptionsChange={setVideoOptions}
                            adOptions={adOptions}
                            onAdOptionsChange={setAdOptions}
                            disabledTooltipByToolId={disabledTooltipByToolId}
                            productImageUrl={inputImages.find((img) => img.fromProduct)?.url ?? productImageUrl}
                        />
                    )}

                    {/* Model-3D workspaces — one mounted instance per visited 3D agent so each
                        agent has isolated internal state. Switching between agents toggles
                        display rather than unmounting, so an in-progress generation in one
                        agent isn't lost or shown when the user is on another agent. The
                        `usesModel3DWorkspace` filter excludes image-to-3d when a product is
                        loaded — that path uses the Creative Studio workspace instead. */}
                    {Array.from(visited3DAgents)
                        .filter((agent) => usesModel3DWorkspace(agent))
                        .map((agent) => {
                            const isActive = state.selectedTool === agent && globalView === 'tool';
                            const matchingOpenedAsset = model3dOpenedAsset && model3dOpenedAsset.toolId === agent ? model3dOpenedAsset : null;
                            return (
                                <Model3DMain key={agent} style={{ display: isActive ? 'flex' : 'none' }}>
                                    <Text3DWorkspaceView
                                        key={`workspace-${agentFreshKeys[agent] ?? 0}-${matchingOpenedAsset?.id ?? 'new'}`}
                                        onBack={goToHome}
                                        initialAgent={agent}
                                        initialPrompt={isActive ? model3dInitialData.prompt : undefined}
                                        initialImageFile={isActive ? model3dInitialData.imageFile : undefined}
                                        initialImageUrl={isActive ? model3dInitialData.imageUrl : undefined}
                                        initialMultiViewReady={isActive ? model3dInitialData.multiViewReady : undefined}
                                        initialMvFiles={isActive ? model3dInitialData.mvFiles : undefined}
                                        openedAsset={
                                            matchingOpenedAsset
                                                ? {
                                                      id: matchingOpenedAsset.id,
                                                      toolId: matchingOpenedAsset.toolId as never,
                                                      thumbnailUrl: matchingOpenedAsset.thumbnailUrl,
                                                      modelUrl: matchingOpenedAsset.modelUrl,
                                                      label: matchingOpenedAsset.label,
                                                      prompt: matchingOpenedAsset.prompt,
                                                      createdAt: matchingOpenedAsset.createdAt
                                                  }
                                                : undefined
                                        }
                                        onOpenCatalog={handleOpenCatalog}
                                        onOpenLibrary={handleOpenLibraryForModel3D}
                                        onOpenImageLibrary={handleOpenImageLibraryForModel3D}
                                        productImageUrl={inputImages.find((img) => img.fromProduct)?.url ?? productImageUrl}
                                        onModelGenerated={handleModel3dGenerated}
                                    />
                                </Model3DMain>
                            );
                        })}

                    {/* Regular tool view — also handles image-to-3d when a product is loaded,
                        so the user can use the standard Creative Studio editor (Images/3D View
                        toggle, pencil-edit, prompt + send) instead of the upload-only landing. */}
                    {globalView === 'tool' && !usesModel3DWorkspace(state.selectedTool) && (
                        <ContentArea>
                            {/* Detect 3D scene availability for catalog-selected products */}
                            {productId && (
                                <Suspense fallback={null}>
                                    <CatalogProduct3DDetector productId={productId} />
                                </Suspense>
                            )}
                            {/* Detect environment from product (DAM/customizer paths — ProductIframe handles catalog) */}
                            {productId && !isFromCatalog && (
                                <Suspense fallback={null}>
                                    <ProductEnvironmentDetector productId={productId} />
                                </Suspense>
                            )}
                            <CenterColumn>
                                <MainContent>
                                    <CanvasArea
                                        view={currentToolView}
                                        results={results}
                                        isGenerating={isGenerating}
                                        generationStartedAt={generationStartedAt}
                                        onGoToEditor={goToEditor}
                                        onGoToGallery={goToGallery}
                                        onDelete={handleDeleteResult}
                                        onUseAsInput={handleUseAsInput}
                                        sourceToolID={state.selectedTool!}
                                        productImages={hasProductInput ? sideImages : undefined}
                                        selectedImageIndex={selectedImageIndex}
                                        has3D={hasProductInput && has3D}
                                        productId={hasProductInput ? productId : undefined}
                                        model3D={hasProductInput ? model3D : undefined}
                                        model3DRootUrl={hasProductInput ? model3DRootUrl : undefined}
                                        viewMode={viewMode}
                                        onViewModeChange={setViewMode}
                                        inputImages={inputImages}
                                        onClearInputImages={() => setInputImages([])}
                                        isProductInputEditing={isProductInputEditing}
                                    />
                                </MainContent>
                                <BottomPanel
                                    toolId={state.selectedTool!}
                                    prompt={state.prompt}
                                    onPromptChange={handlePromptChange}
                                    selectionState={selectionState}
                                    onPresetSelect={handlePresetSelect}
                                    onGenerate={handleGenerate}
                                    onStop={handleStopGeneration}
                                    isGenerating={isGenerating}
                                    canGenerate={canGenerate}
                                    inputPresetType={currentToolConfig?.inputPresetType}
                                    inputImages={inputImages}
                                    onInputImagesChange={setInputImages}
                                    isProductMode={hasProductInput}
                                    guideLink={currentToolConfig?.guideLink}
                                    promptPlaceholder={currentToolConfig?.promptPlaceholder}
                                    onOpenCatalog={handleOpenCatalog}
                                    onOpenLibrary={handleOpenLibraryDialog}
                                    onToggleInputEditor={() => {
                                        setIsProductInputEditing((prev) => {
                                            if (!prev) goToEditor();
                                            return !prev;
                                        });
                                    }}
                                    isAutoCapturing={isAutoCapturing}
                                    isProductInputEditing={isProductInputEditing}
                                    onRemoveProduct={hasProductInput ? handleRemoveProduct : undefined}
                                />
                            </CenterColumn>
                            <SettingsPanel
                                toolId={state.selectedTool!}
                                videoOptions={videoOptions}
                                onVideoOptionsChange={setVideoOptions}
                                adOptions={adOptions}
                                onAdOptionsChange={setAdOptions}
                            />
                        </ContentArea>
                    )}
                </>
            )}

            {local3dPreviewIndex !== null && (
                <Local3DPreviewDialog
                    items={model3dGeneratedAssets}
                    index={local3dPreviewIndex}
                    onClose={() => setLocal3dPreviewIndex(null)}
                    onIndexChange={setLocal3dPreviewIndex}
                />
            )}
        </Container>
    );
};

/**
 * Main AI Studio content component.
 * Wraps with Viewer3DProvider to enable 3D capture functionality.
 */
export const AIStudioContent: FC<AIStudioContentProps> = (props) => {
    return (
        <Viewer3DProvider>
            <AIStudioContentInner {...props} />
        </Viewer3DProvider>
    );
};

export default AIStudioContent;
